import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
} from '@mui/material';
import BaseDialog from '../BaseDialog';
import useApiControl from '../../../common/useApiControl';

const parseChoices = (answers) => {
  if (!answers) {
    return [];
  }

  try {
    return JSON.parse(answers);
  } catch (error) {
    console.error('Error parsing answers JSON', error);
    return [];
  }
};

const ResultCorrectionDialog = ({ isVisible, onClose, round, onCorrected, dialogMode, containerRef }) => {
  const choices = useMemo(() => parseChoices(round?.answers), [round?.answers]);
  const [result, setResult] = useState('');
  const [mode, setMode] = useState('correct');
  const [confirmInvalidation, setConfirmInvalidation] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const savedResult = round?.result !== null && round?.result !== undefined
      ? String(round.result)
      : '';
    setResult(savedResult);
    setMode('correct');
    setConfirmInvalidation(false);
    setMessage('');
  }, [round?.id, round?.result, isVisible]);

  const params = useMemo(() => ({
    eid: round?.event_id,
    result,
  }), [round?.event_id, result]);

  const { executeRequest, loading } = useApiControl({
    action: '/correct_round_result',
    params,
    method: 'POST',
  });

  const { executeRequest: invalidateRound, loading: invalidating } = useApiControl({
    action: '/invalidate_disputed_round',
    params: { eid: round?.event_id },
    method: 'POST',
  });

  const handleSubmit = async () => {
    try {
      setMessage('');
      await executeRequest();
      await onCorrected();
      onClose();
    } catch (error) {
      setMessage('結果を訂正できませんでした。画面を更新して、現在の確認状態を確認してください。');
    }
  };

  const handleInvalidate = async () => {
    try {
      setMessage('');
      await invalidateRound();
      await onCorrected();
      onClose();
    } catch (error) {
      setMessage('ラウンドを不成立にできませんでした。画面を更新して、現在の確認状態を確認してください。');
    }
  };

  const currentResult = round?.result !== null && round?.result !== undefined
    ? String(round.result)
    : '';
  const currentLabel = currentResult !== '' ? choices[Number(currentResult)] : '';
  const selectedLabel = result !== '' ? choices[Number(result)] : '';
  const changed = result !== '' && result !== currentResult;
  const correctionLimitReached = Number(round?.result_revision_count ?? 0) >= 1;
  const isHeld = round?.review_status === 'held';

  return (
    <BaseDialog isVisible={isVisible} onClose={onClose} dialogMode={dialogMode} containerRef={containerRef}>
      <Box sx={{ width: 400, maxWidth: '100%', p: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 17, mb: 1 }}>
          結果確認の対応
        </Typography>
        {!isHeld ? (
          <>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              結果の訂正・不成立は、再確認依頼が基準に達した場合だけ実行できます。
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" onClick={onClose}>
                閉じる
              </Button>
            </Box>
          </>
        ) : (
          <>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
          再確認依頼が基準に達したため、結果を訂正するか、このラウンドを不成立にしてください。
        </Typography>
        {message && <Alert severity="warning" sx={{ mb: 1.5 }}>{message}</Alert>}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            現在の選択結果
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {currentLabel || '未選択'}
          </Typography>
        </Box>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">対応方法</FormLabel>
          <RadioGroup value={mode} onChange={(event) => {
            setMode(event.target.value);
            setConfirmInvalidation(false);
            setMessage('');
          }}>
            <FormControlLabel
              value="correct"
              control={<Radio />}
              label="結果を訂正する"
              disabled={correctionLimitReached}
            />
            <FormControlLabel value="invalidate" control={<Radio />} label="ラウンドを不成立にする" />
          </RadioGroup>
        </FormControl>

        {mode === 'correct' && (
          <>
            {correctionLimitReached ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                訂正は1回までです。ラウンドを不成立にしてください。
              </Alert>
            ) : (
              <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>
                訂正結果に対する再確認時間が新しく始まります。
              </Typography>
            )}
            <FormControl component="fieldset">
              <FormLabel component="legend">変更後の選択結果</FormLabel>
              <RadioGroup value={result} onChange={(event) => setResult(event.target.value)}>
                {choices.map((choice, index) => {
                  const value = String(index);
                  return (
                    <FormControlLabel
                      key={index}
                      value={value}
                      control={<Radio />}
                      label={choice}
                      disabled={value === currentResult || correctionLimitReached}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                変更する選択結果
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {selectedLabel || '未選択'}
              </Typography>
            </Box>
          </>
        )}

        {mode === 'invalidate' && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              このラウンドを不成立にしますか？
              <br />
              すべてのベットを返却し、勝敗やランキングには反映しません。
              <br />
              不成立にした後は結果を登録できません。
            </Alert>
            {!confirmInvalidation ? (
              <Button color="warning" variant="outlined" onClick={() => setConfirmInvalidation(true)}>
                不成立操作を確認
              </Button>
            ) : (
              <Alert severity="error">
                不可逆操作です。実行する場合は下のボタンを押してください。
              </Alert>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', mt: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={loading || invalidating}>
            キャンセル
          </Button>
          {mode === 'correct' ? (
            <Button variant="contained" onClick={handleSubmit} disabled={!changed || loading || correctionLimitReached}>
              この結果に訂正
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={handleInvalidate}
              disabled={!confirmInvalidation || invalidating}
            >
              不成立にしてベットを返却
            </Button>
          )}
        </Box>
          </>
        )}
      </Box>
    </BaseDialog>
  );
};

export default ResultCorrectionDialog;
